import { Check, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { InteractiveDashboard } from "./InteractiveDashboard";

export const HeroSection = ({ onRequestDemo }) => {
  const valueProps = [
    {
      text: "Up to 40-60% less standing privilege",
      subtext: "— shrink blast radius in 2 weeks",
    },
    {
      text: "Drift detection in <2 hours",
      subtext: "— from 18-24 hours today",
    },
    {
      text: "Pre-production sandbox + real-time behavioral monitoring",
      subtext: "— stop compromise before it happens",
    },
  ];

  const complianceBadges = ["EU AI Act", "SOC 2", "HIPAA Ready"];

  return (
    <section 
      className="relative pt-32 sm:pt-36 lg:pt-40 pb-16 sm:pb-20 lg:pb-24 px-6 lg:px-10 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Glows */}
      <div className="absolute top-0 left-0 w-full h-full opacity-30 pointer-events-none">
        <div className="hero-glow" />
        <div className="hero-glow-secondary" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        {/* Left Content */}
        <div className="reveal">
          {/* Badge */}
          <div 
            className="inline-flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-1.5 rounded-full bg-slate-900/50 border border-slate-700 text-violet-400 text-[9px] sm:text-[10px] font-extrabold uppercase tracking-[0.2em] sm:tracking-[0.3em] mb-6 lg:mb-8"
            data-testid="hero-badge"
          >
            <Shield className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            AI Agent Governance Platform
          </div>
          
          {/* Headline */}
          <h1 
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-white tracking-tighter mb-4 lg:mb-6 leading-[0.95]"
            data-testid="hero-headline"
          >
            Total Visibility. <br />
            <span className="text-violet-500">Absolute Integrity.</span>
          </h1>
          
          {/* Subheadline - More visible */}
          <p 
            className="text-base sm:text-lg text-slate-200 max-w-xl mb-6 lg:mb-8 leading-relaxed font-semibold"
            data-testid="hero-subheadline"
          >
            The Agentic Entitlement Engine built for 2026 EU AI Act compliance and enterprise-scale AI agents
          </p>
          
          {/* Value Props with Emerald Checkmarks */}
          <div className="space-y-3 mb-8 lg:mb-10 max-w-xl" data-testid="hero-value-props">
            {valueProps.map((prop, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="mt-0.5 flex-shrink-0">
                  <Check className="w-5 h-5 text-emerald-400" strokeWidth={2.5} />
                </div>
                <p className="text-sm sm:text-base text-white font-medium leading-relaxed">
                  <span className="font-extrabold">{prop.text}</span> {prop.subtext}
                </p>
              </div>
            ))}
          </div>

          {/* Compliance Badges */}
          <div className="flex items-center gap-4 mb-8 lg:mb-10 flex-wrap" data-testid="compliance-badges">
            {complianceBadges.map((badge, index) => (
              <span 
                key={index}
                className="flex items-center gap-1.5 text-xs text-slate-300"
              >
                <Shield className="w-3.5 h-3.5 text-violet-500" />
                {badge}
              </span>
            ))}
          </div>
          
          {/* CTA Buttons - Same Size */}
          <div className="flex flex-col sm:flex-row gap-4" data-testid="hero-ctas">
            <Button 
              onClick={onRequestDemo}
              className="h-14 px-8 bg-white text-black font-extrabold rounded-xl hover:bg-slate-200 transition-all shadow-2xl flex items-center justify-center gap-3 group text-xs uppercase tracking-widest glow-violet w-full sm:w-48"
              data-testid="request-demo-hero-btn"
            >
              Request Demo
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="group-hover:translate-x-1 transition-transform">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </Button>
            <a 
              href="#waitlist" 
              className="h-14 px-8 bg-slate-900/50 text-white font-extrabold rounded-xl border border-slate-700 hover:bg-slate-800 transition-all flex items-center justify-center text-xs uppercase tracking-widest w-full sm:w-48"
              data-testid="join-waitlist-btn"
            >
              Join Waitlist
            </a>
          </div>
        </div>

        {/* Right Content - Interactive Dashboard */}
        <div className="reveal reveal-delay-2 lg:pl-4">
          <InteractiveDashboard />
        </div>
      </div>
    </section>
  );
};
